import { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, Search, X, UserPlus } from 'lucide-react'
import Popover from './Popover'
import { useApi } from '../hooks/useApi'
import { getInitials, isValidEmail } from '../lib/utils'
import type { User, UserRef } from '../types'

interface UserPickerProps {
  value: UserRef | undefined
  onChange: (user: UserRef | undefined) => void
  placeholder?: string
  /** When true the picker can't be cleared (no X button). */
  required?: boolean
  /** When true, whatever is typed in the search box can be selected directly
   *  (as a name, or an email if it looks like one) for people not in the
   *  directory. No Entra permission needed - used wherever the person is just
   *  a stored label (risk owner, report VP/PM). */
  allowFreeText?: boolean
  /** Sub-label under the free-text option. Defaults to the risk-owner wording. */
  freeTextHint?: string
}

/**
 * Searchable dropdown over the self-seeded user directory (`GET /users`).
 * Returns a complete `UserRef` with the canonical Entra `oid` so callers
 * never have to invent their own identifier. The picker fetches the
 * directory once on mount and filters client-side - directories are small
 * enough that round-tripping per keystroke isn't worth the complexity.
 *
 * With `allowFreeText`, whatever is typed (2+ chars) can also be picked as-is
 * - a name, or an email if it looks like one - for people not in the
 * directory. Used for the risk owner, which is just a stored label.
 */
export default function UserPicker({ value, onChange, placeholder = 'Select a user', required = false, allowFreeText = false, freeTextHint = 'Add as owner' }: UserPickerProps) {
  const api = useApi()
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState<User[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  // Lazy fetch - only when the dropdown opens for the first time.
  // `loading` is intentionally NOT in the deps: setLoading(true) re-runs the
  // effect, the cleanup cancels the in-flight fetch, and setLoading(false)
  // never lands -> the picker is stuck on "Loading users…" forever.
  useEffect(() => {
    if (!open || users !== null) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api.getUsers()
      .then((rows: any) => {
        if (cancelled) return
        // Defensive: backend may return an error body (object) instead of a list.
        setUsers(Array.isArray(rows) ? rows : [])
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load users')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [open, users])

  // Directory matches for the current query.
  const options = useMemo<UserRef[]>(() => {
    const q = query.trim().toLowerCase()
    const local = !users ? [] : (!q ? users : users.filter(u =>
      (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q),
    ))
    return local.map(u => ({ oid: u.oid, name: u.name, email: u.email }))
  }, [users, query])

  const select = (u: UserRef) => {
    onChange({ oid: u.oid, name: u.name, email: u.email })
    setOpen(false)
    setQuery('')
  }

  // Free-text fallback: whatever is typed (2+ chars) that doesn't exactly
  // match a directory entry can be picked as-is - a name, or an email if it
  // looks like one. The text doubles as the oid (there's no Entra lookup here)
  // - fine because the risk owner is only ever a stored label.
  const trimmedQuery = query.trim()
  const qLower = trimmedQuery.toLowerCase()
  const showFreeText = allowFreeText
    && trimmedQuery.length >= 2
    && !options.some(o => (o.name || '').toLowerCase() === qLower || (o.email || '').toLowerCase() === qLower)

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(undefined)
  }

  const close = () => {
    setOpen(false)
    setQuery('')
  }

  const optionClass = 'w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none transition-colors'

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={`w-full flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-danone-cyan transition-colors ${
          value ? 'border-slate-200 bg-white text-slate-800' : 'border-slate-200 bg-slate-50 text-slate-500'
        }`}
      >
        {value ? (
          <>
            <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
              {getInitials(value.email || value.name || 'U')}
            </span>
            <span className="flex-1 text-left truncate">
              {value.name || value.email}
            </span>
            {!required && (
              <span
                onClick={clear}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                aria-label="Clear selection"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
          </>
        ) : (
          <span className="flex-1 text-left">{placeholder}</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      <Popover open={open} anchorRef={ref} onClose={close} width="anchor" className="min-w-[16rem]">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 flex-shrink-0">
            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <input
              autoFocus
              data-popover-search
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name or email"
              aria-label="Search by name or email"
              className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-slate-400"
            />
          </div>
          <div role="listbox" className="max-h-72 min-h-0 overflow-y-auto py-1">
            {loading && <div className="px-3 py-2 text-xs text-slate-400">Loading users…</div>}
            {error && <div className="px-3 py-2 text-xs text-red-600">{error}</div>}
            {!loading && !error && options.length === 0 && !showFreeText && (
              <div className="px-3 py-2 text-xs text-slate-400">
                {allowFreeText ? 'Type a name to search or add anyone'
                  : trimmedQuery.length >= 2 ? 'No matches'
                  : 'No users in directory yet'}
              </div>
            )}
            {!loading && !error && options.map(u => {
              const isSelected = value?.oid === u.oid
              return (
                <button
                  key={u.oid}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  data-option
                  onClick={() => select(u)}
                  className={`${optionClass} ${isSelected ? 'bg-cyan-50' : ''}`}
                >
                  <span className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                    {getInitials(u.email || u.name || 'U')}
                  </span>
                  <span className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 truncate">{u.name || '-'}</div>
                    {u.email && <div className="text-[11px] text-slate-400 truncate">{u.email}</div>}
                  </span>
                </button>
              )
            })}
            {showFreeText && (
              <button
                type="button"
                role="option"
                aria-selected={false}
                data-option
                onClick={() => {
                  const isEmail = isValidEmail(trimmedQuery)
                  select({
                    oid: qLower,
                    name: isEmail ? undefined : trimmedQuery,
                    email: isEmail ? trimmedQuery : undefined,
                  })
                }}
                className={`${optionClass} border-t border-slate-100`}
              >
                <span className="w-7 h-7 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-3.5 h-3.5" />
                </span>
                <span className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">Use "{trimmedQuery}"</div>
                  <div className="text-[11px] text-slate-400">{freeTextHint}</div>
                </span>
              </button>
            )}
          </div>
      </Popover>
    </div>
  )
}
